import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMatch } from 'react-router-dom'
import type { SearchOption } from '@jana-technology/platform-ui'

import { IContinuousRoute, ModalType } from '$/types'
import DetailsModal from 'common/components/Modal/DetailsModal'

import {
  EMPTY_LOCAL_FILTER,
  EMPTY_ROUTE
} from 'features/RiskManagement/Components/LocalFilter'
import ConfirmFilterModal from 'features/RiskManagement/Components/LocalFilter/ConfirmModal'
import {
  selectPreviousSegments,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import {
  setPreviousLineIdSync,
  setPreviousSegmentsSync,
  setSavedFilterSync
} from 'features/RiskManagement/Components/LocalFilter/localFilterSlice'

import FilterDetails from './FilterDetails'
import ActionBar from './LocalFilterActionBar'

// #region helpers
const selectOptionMapper = (selection: SearchOption) => {
  return {
    name: selection.label as string,
    id: 0,
    code: String(selection.value)
  }
}

const removeEmptyRoutes = (route: IContinuousRoute) => route.id > -1
// #endregion

const LocalFilterModalWrapper = ({ onClose }: ModalType) => {
  const dispatch = useDispatch()
  const localFilter = useSelector(selectSavedFilter)
  const prevSegments = useSelector(selectPreviousSegments)

  const match = useMatch('/risk-management/assessments/routes/:routeId')
  const params = match?.params

  const [selectedRoutes, setSelectedRoutes] = useState<IContinuousRoute[]>([
    EMPTY_ROUTE
  ])

  const [selectedHmca, setSelectedHmca] = useState<SearchOption[]>([])

  const [selectedClassLocations, setSelectedClassLocations] = useState<
    SearchOption[]
  >([])

  const [selectedRiskLevels, setSelectedRiskLevels] = useState<SearchOption[]>(
    []
  )

  const [isConfirmFilterModalOpen, setIsConfirmFilterModalOpen] =
    useState<boolean>(false)

  const [isLocalFilterStateChanged, setIsLocalFilterStateChanged] =
    useState<boolean>(false)

  const [canFilterBeApplied, setCanFilterBeApplied] = useState<boolean>(true)

  const onHMCASelection = useCallback<(selection: SearchOption[]) => void>(
    (selection) => {
      setIsLocalFilterStateChanged(true)

      if (selection) {
        setSelectedHmca(selection)
      } else {
        setSelectedHmca([])
      }
    },
    []
  )

  const onClassLocationSelection = useCallback<
    (selection: SearchOption[]) => void
  >((selection) => {
    if (selection) {
      setIsLocalFilterStateChanged(true)

      setSelectedClassLocations(selection)
    } else {
      setSelectedClassLocations([])
    }
  }, [])

  const onRiskLevelSelection = useCallback<(selection: SearchOption[]) => void>(
    (selection) => {
      setIsLocalFilterStateChanged(true)

      if (selection) {
        setSelectedRiskLevels(selection)
      } else {
        setSelectedRiskLevels([])
      }
    },
    []
  )

  // Sets routes, hmca, class location, and risk level state to match redux store
  const reloadStateFromReduxStore = useCallback(() => {
    setSelectedRoutes(
      localFilter.segments.length ? localFilter.segments : [EMPTY_ROUTE]
    )

    setSelectedHmca(
      localFilter.hmca.map((item) => {
        return { label: item.name, value: item.code }
      })
    )

    setSelectedClassLocations(
      localFilter.classLocations.map((item) => {
        return { label: item.name, value: item.code }
      })
    )

    setSelectedRiskLevels(
      localFilter.riskLevels.map((item) => {
        return { label: item.name, value: item.code }
      })
    )
  }, [
    localFilter.classLocations,
    localFilter.hmca,
    localFilter.riskLevels,
    localFilter.segments
  ])

  // Updates state to match the local filter that is applied
  useEffect(() => {
    reloadStateFromReduxStore()
  }, [reloadStateFromReduxStore])

  const prevSegmentsLength = prevSegments?.length

  // ! CARRYOVER: resetting logic for no routeId present
  // ? how does routeId affect syncing localFilter with prevSegments
  // ? why was prevSegments.length used
  useEffect(() => {
    // if we are in Line View mode, save previous segments
    if (params?.routeId != null) {
      dispatch(setPreviousSegmentsSync(localFilter.segments))
    }
  }, [dispatch, localFilter.segments, params?.routeId, prevSegmentsLength])

  const applyFilter = (): void => {
    onClose()

    const filter = { ...EMPTY_LOCAL_FILTER }

    filter.segments = selectedRoutes.filter(removeEmptyRoutes)

    filter.hmca = selectedHmca.map(selectOptionMapper)

    filter.classLocations = selectedClassLocations.map(selectOptionMapper)

    filter.riskLevels = selectedRiskLevels.map(selectOptionMapper)

    // ! CARRYOVER: extracted logic from useEffect
    // ? not sure if the Line/System View split is needed
    if (params?.routeId != null) {
      filter.segments = selectedRoutes.filter(
        (route) => String(route.id) === params.routeId
      )

      dispatch(setSavedFilterSync(filter))
      dispatch(setPreviousLineIdSync(params.routeId))
    } else {
      // add segments in the current filter to the new filter
      dispatch(setSavedFilterSync(filter))
      // ? why are we clearing previous segments
      dispatch(setPreviousSegmentsSync([]))
      dispatch(setPreviousLineIdSync(''))
    }
  }

  const resetFilter = (): void => {
    setSelectedRoutes([EMPTY_ROUTE])
    setSelectedHmca([])
    setSelectedClassLocations([])
    setSelectedRiskLevels([])
  }

  const openConfirmFilterModal = (): void => {
    if (isLocalFilterStateChanged && canFilterBeApplied) {
      setIsConfirmFilterModalOpen(true)
    } else {
      onClose()
    }
  }

  return (
    <>
      <ConfirmFilterModal
        isConfirmFilterModalOpen={isConfirmFilterModalOpen}
        closeConfirmFilterModal={() => setIsConfirmFilterModalOpen(false)}
        closeModal={onClose}
        applyFilter={applyFilter}
        reloadStateFromReduxStore={reloadStateFromReduxStore}
      />
      <DetailsModal
        title='Filters'
        open={true}
        close={openConfirmFilterModal}
        footer={
          <ActionBar
            canApply={canFilterBeApplied}
            applyFilter={applyFilter}
            resetFilter={resetFilter}
            cancelFilter={openConfirmFilterModal}
          />
        }
      >
        <div className='flex overflow-auto border-y'>
          <FilterDetails
            localFilter={localFilter}
            selectedRoutes={selectedRoutes}
            setSelectedRoutes={setSelectedRoutes}
            selectedHmca={selectedHmca}
            onHMCASelection={onHMCASelection}
            selectedClassLocations={selectedClassLocations}
            onClassLocationSelection={onClassLocationSelection}
            selectedRiskLevels={selectedRiskLevels}
            onRiskLevelSelection={onRiskLevelSelection}
            setIsLocalFilterStateChanged={setIsLocalFilterStateChanged}
            paramsRouteId={params?.routeId}
            setCanFilterBeApplied={setCanFilterBeApplied}
          />
        </div>
      </DetailsModal>
    </>
  )
}

export default LocalFilterModalWrapper
